import { Mail } from "lucide-react";
import { FadeUp, SectionTag } from "@/components/motion";
import { useBookingForm } from "@/hooks/useBookingForm";
import { BookingTypeToggle } from "@/components/booking/BookingTypeToggle";
import { ServiceToggle } from "@/components/booking/ServiceToggle";
import { DateTimePicker } from "@/components/booking/DateTimePicker";
import { INPUT_CLS } from "@/lib/formStyles";

export default function BookingForm({ prefill }) {
  const { form, set, submitting, calendarOpen, setCalendarOpen, submit } = useBookingForm(prefill);

  return (
    <section id="booking" data-testid="booking-section" className="py-20 sm:py-28 lg:py-36 bg-sand/70 border-y border-ink/8">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <FadeUp className="lg:col-span-5">
          <SectionTag>Booking</SectionTag>
          <h2 className="mt-3 font-serif tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Reserve<br />
            <span className="italic text-gold">your curated experience.</span>
          </h2>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-stone-600">
            Tell us what you're dreaming of — a solo session or a full celebration — and we'll
            confirm your time personally. Every enquiry lands directly in our studio inbox.
          </p>
          <a
            data-testid="booking-email-link"
            href="mailto:booking@faithfullylinked.app"
            className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-gold/25 bg-gold/10 px-5 py-4 hover:bg-gold/15 transition-colors duration-300"
          >
            <Mail size={18} className="text-gold" />
            <span className="text-sm font-medium text-ink">booking@faithfullylinked.app</span>
          </a>
          <p className="mt-6 text-[10px] font-mono uppercase tracking-[0.22em] text-stone-400">
            By appointment · Tue–Sat · Replies within 24h
          </p>
        </FadeUp>

        <FadeUp delay={0.15} className="lg:col-span-7">
          <form
            data-testid="booking-form"
            onSubmit={submit}
            className="bg-white border border-ink/8 rounded-[2rem] p-6 sm:p-10 shadow-[0_24px_60px_rgba(28,25,23,0.06)]"
          >
            <BookingTypeToggle value={form.bookingType} onChange={(v) => set("bookingType", v)} />
            <ServiceToggle value={form.service} onChange={(v) => set("service", v)} />

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required data-testid="booking-name-input" placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT_CLS} />
              <input required type="email" data-testid="booking-email-input" placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT_CLS} />
              <input data-testid="booking-phone-input" placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={INPUT_CLS} />
              {form.bookingType === "party" ? (
                <input type="number" min="2" data-testid="booking-party-size-input" placeholder="Approx. party size" value={form.partySize} onChange={(e) => set("partySize", e.target.value)} className={INPUT_CLS} />
              ) : (
                <div className="hidden sm:block" />
              )}
            </div>

            <DateTimePicker
              date={form.date}
              time={form.time}
              onDateChange={(d) => set("date", d)}
              onTimeChange={(t) => set("time", t)}
              calendarOpen={calendarOpen}
              setCalendarOpen={setCalendarOpen}
            />

            <textarea
              data-testid="booking-message-input"
              rows={4}
              placeholder="Tell us about the occasion, metal preferences, or anything else…"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              className={`${INPUT_CLS} mt-4 resize-none`}
            />

            <button
              type="submit"
              data-testid="booking-submit-button"
              disabled={submitting}
              className="mt-6 w-full rounded-full bg-gold text-white py-4 text-sm font-medium tracking-wide hover:bg-gold-dark transition-colors duration-300 shadow-[0_10px_30px_rgba(26,56,140,0.3)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Send Booking Enquiry"}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
